import {Barcode, Language} from "@variocube/app-api";
import {
    BackButton,
    Checkbox,
    classNames,
    createEmailKeyboardLayout,
    EnglishKeyboardLayout,
    GermanKeyboardLayout,
    Keyboard,
    layout,
    PhoneKeyboardLayout,
    PushButton,
    TextInput,
    theme,
    typography,
    useBarcodeListener,
    useStorageItemValue,
    useTypeRef,
    View,
    ViewContent,
    ViewFooter,
    ViewFooterItem,
    ViewHeader,
    ViewHeaderItem,
    ViewTitle
} from "@variocube/cube-ui";
import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {stylesheet} from "typestyle";
import {Delivery, DeliveryFieldConfig, ReasonForReturnEnum, ReturnDeliveryConfig} from "./types";
import {useLocalization} from "../i18n";


interface ReturnDialogProps {
    onBack: () => void;
    onSubmit: (deliveryNoteId: string, reasonForReturn: ReasonForReturnEnum | undefined  , email:string,phone:string, customerId: string,positionNumbers: string,addInfo: string) => void;
    config: ReturnDeliveryConfig;
    delivery?: Delivery;
	label?: string;
}

enum CustomerField {
    DeliveryNoteId = 'DeliveryNoteId',
    ReasonForReturn = 'ReasonForReturn',
    Email = 'Email',
    Phone = 'Phone',
    CustomerId = 'CustomerId',
    Position = 'Position',
    AddInfo = 'AddInfo'
}

export function ReturnDeliveryDialog({onBack, onSubmit, config, delivery, label} : ReturnDialogProps) {
    const {t, language} = useLocalization();
    const [email, setEmail] = useState('');
    const [deliveryNoteId, setDeliveryNoteId] = useState(delivery?.order?.deliveryNoteId ?? '');
    const [phone, setPhone] = useState('');
    const [customerId, setCustomerId] = useState('');
    const [positionNumbers, setPositionNumbers] = useState('');
    const [addInfo, setAddInfo] = useState('');
    const [reasonForReturn,setReasonForReturn] = useState<ReasonForReturnEnum>();
    const deliveryConfigParams = config.parameters ?? {};

    const [error,setError] = useState('');

    const emailRef = useRef<HTMLInputElement>();

    const [deliveryNoteIdType,setDeliveryNoteIdType] = useTypeRef();
    const [emailType,setEmailType] = useTypeRef();
    const [phoneType,setPhoneType] = useTypeRef();
    const [customerIdType, setCustomerIdType] = useTypeRef();
    const [positionNumbersType, setPositionNumbersType] = useTypeRef();
    const [addInfoType, setAddInfoType] = useTypeRef();

    const [field, setField] = useState<CustomerField>();

    const deliveries = useStorageItemValue<Delivery[]>("deliveries") || [];

    const keyboardLayout = language === Language.DE ? GermanKeyboardLayout : EnglishKeyboardLayout;

    function getParam(param: keyof typeof deliveryConfigParams): DeliveryFieldConfig {
        return config.parameters?.[param] ?? { enabled: false, mandatory: false, verification: false };
    }

    function handleSubmit() {
        let msg = '';
        if (deliveryNoteId.length === 0 && getParam('deliveryNoteId').enabled &&  getParam('deliveryNoteId').mandatory) {
            msg = t("DeliveryNoteId") + ' ' + t("Missing");
        }
        if (reasonForReturn === undefined &&  getParam('returnReason').enabled &&  getParam('returnReason').mandatory) {
            msg += ' ' + t("ReasonForReturn") + ' ' + t("Missing");
        }
        if (email.length === 0 &&  getParam('email').enabled &&  getParam('email').mandatory) {
            msg += ' ' + t("Email") + ' ' + t("Missing");
        }
        if (phone.length === 0 &&  getParam('phone').enabled &&  getParam('phone').mandatory) {
            msg += ' ' + t("Phone") + ' ' + t("Missing");
        }
        if (customerId.length === 0 &&  getParam('customerId').enabled &&  getParam('customerId').mandatory) {
            msg += ' ' + t("CustomerId") + ' ' + t("Missing");
        }
        if (positionNumbers.length === 0 &&  getParam('positionNumber').enabled &&  getParam('positionNumber').mandatory) {
            msg += ' ' + t("PositionNumbers") + ' ' + t("Missing");
        }
        if (addInfo.length === 0 &&  getParam('additionalInformation').enabled &&  getParam('additionalInformation').mandatory) {
            msg += ' ' + t("AdditionalInfo") + ' ' + t("Missing");
        }
        setError(msg)
        if (msg === '') {
            onSubmit(deliveryNoteId, reasonForReturn, email, phone, customerId, positionNumbers, addInfo);
        }
    }

    function handleBarcode (code: Barcode)  {
        try {
            setDeliveryNoteId(code.code);
        } catch (error) {
            console.error("Error retrieving  barcode");
        }
    }

    useBarcodeListener(handleBarcode);

    function onChangeDeliveryNote(val: string){
        console.log('deliveries:' + deliveries.length)
        let shit = val.substring(0,10)
        setDeliveryNoteId(shit);
    }

	const emptyParams = useMemo(() => Object.entries(config.parameters ?? {}).filter(([_, v]) => v.enabled).length == 0, [config]);

	// automatically submit when return was created from home screen and no params were configured
	useEffect(() => {
		if (emptyParams && !delivery) {
			handleSubmit();
		}
	}, [delivery, emptyParams])

    return (
        <View>
            <ViewHeader>
                <ViewHeaderItem>
                    <BackButton onClick={onBack} label={t("Back")}/>
                </ViewHeaderItem>
                <ViewHeaderItem fill>
                    <ViewTitle>{label ?? t("Return")}</ViewTitle>
                </ViewHeaderItem>
            </ViewHeader>
			<ViewContent className={classNames(styles.content, layout.vertical, layout.center)}>
				<div className={styles.contentInner}>
					{emptyParams && (
						<p className={styles.promptText}>{t('ReturnDeliveryPrompt')}</p>
					)}

					{getParam('deliveryNoteId').enabled &&
						<TextInput
							outline={true}
							placeholder={t("DeliveryNoteId")}
							className={classNames(styles.textInput)}
							value={deliveryNoteId}
							typeRef={setDeliveryNoteIdType}
							onChange={onChangeDeliveryNote}
							onFocus={() => setField(CustomerField.DeliveryNoteId)}
						/>
					}
					{getParam('returnReason').enabled &&
						<div className={layout.paddingLarge}>
							<Checkbox inline={true} checked={reasonForReturn === ReasonForReturnEnum.WrongItem}
									  onChange={() => setReasonForReturn(ReasonForReturnEnum.WrongItem)}>{t("WrongItem")}</Checkbox>
							&emsp; &emsp;
							<Checkbox inline={true} checked={reasonForReturn === ReasonForReturnEnum.DefectItem}
									  onChange={() => setReasonForReturn(ReasonForReturnEnum.DefectItem)}>{t("DefectItem")}</Checkbox>
						</div>
					}
					{getParam('email').enabled &&
						<TextInput
							outline={true}
							placeholder={t("Email")}
							className={classNames(styles.textInput)}
							value={email}
							typeRef={setEmailType}
							inputRef={emailRef}
							onChange={value => {
								setEmail(value.substring(0, 999))
							}}
							onFocus={() => setField(CustomerField.Email)}

						/>
					}
					{getParam('phone').enabled &&
						<TextInput
							outline={true}
							placeholder={t("Phone")}
							className={classNames(styles.textInput)}
							value={phone}
							typeRef={setPhoneType}
							onChange={value => {
								setPhone(value.substring(0, 99))
							}}
							onFocus={() => setField(CustomerField.Phone)}

						/>
					}
					{getParam('customerId').enabled &&
						<TextInput
							outline={true}
							placeholder={t("CustomerId")}
							className={classNames(styles.textInput)}
							value={customerId}
							typeRef={setCustomerIdType}
							onChange={value => setCustomerId(value.substring(0, 255))}
							onFocus={() => setField(CustomerField.CustomerId)}

						/>
					}
					{getParam('positionNumber').enabled &&
						<TextInput
							outline={true}
							placeholder={t("PositionNumbers")}
							className={classNames(styles.textInput)}
							value={positionNumbers}
							typeRef={setPositionNumbersType}
							onChange={value => setPositionNumbers(value.substring(0, 255))}
							onFocus={() => setField(CustomerField.Position)}

						/>
					}
					{getParam('additionalInformation').enabled &&
						<TextInput
							outline={true}
							placeholder={t("AdditionalInfo")}
							className={classNames(styles.textInput)}
							value={addInfo}
							typeRef={setAddInfoType}
							onChange={value => setAddInfo(value.substring(0, 255))}
							onFocus={() => setField(CustomerField.AddInfo)}

						/>
					}
					<div className={classNames(styles.Error, typography.colorError)}>{error}</div>
					<div>
						<PushButton primary onClick={handleSubmit} className={classNames(styles.action)}>
							{emptyParams ? t("Confirm") : t("Submit")}
						</PushButton>
					</div>
				</div>
			</ViewContent>
			{field && (
				<ViewFooter className={theme.lessContrast}>
					<ViewFooterItem fill className={styles.keyboardContainer}>
						{field === CustomerField.DeliveryNoteId && (
							<Keyboard onType={deliveryNoteIdType} layout={keyboardLayout}/>
						)}
						{field === CustomerField.Email && (
							<Keyboard onType={emailType} layout={createEmailKeyboardLayout(true)}/>
						)}
						{field === CustomerField.Phone && (
							<Keyboard onType={phoneType} layout={PhoneKeyboardLayout}/>
						)}
						{field === CustomerField.CustomerId && (
							<Keyboard onType={customerIdType} layout={keyboardLayout}/>
						)}
						{field === CustomerField.Position && (
							<Keyboard onType={positionNumbersType} layout={keyboardLayout}/>
						)}
						{field === CustomerField.AddInfo && (
							<Keyboard onType={addInfoType} layout={keyboardLayout}/>
						)}
					</ViewFooterItem>
				</ViewFooter>
			)}
        </View>
    );
}

const styles = stylesheet({
    label: {
        fontSize: "1.5rem",
    },
    textInput: {
        fontWeight: "bold",
        width: "90%",
        height: "4rem",
        fontSize: "2em",
        margin: "0.3em"
    },
    keyboard: {
        backgroundColor: "transparent",
        fontSize: "1.25rem",
    },
    content: {
        display: "flex",
        justifyContent: "flex-start"
    },
    contentInner: {
        width: '100%',
        // minHeight: '100dvh',
        display: "flex",
        flexDirection: 'column',
        alignItems: "center",
        // problem because the v. keyboards covers the fields. 
        // justifyContent: "center",
    },
    keyboardContainer: {
        padding: "0.25rem"
    },
    Checkbox: {
        height: "1em",
        width: "1em",
        display: "inline",
        margin: "1em"
    },
    Error: {
        textAlign: "center",
        fontSize: "2em",
        fontWeight: "bold",
    },
    action: {
        cursor: "pointer"
    },
	promptText: {
		fontSize: "1.5rem",
		lineHeight: "1.2em",
	}
})
